import { useSelector, useDispatch } from "react-redux";
import apiClient from "../utils/ApiClient";
import { updateStore } from "./reducers";

export const useAppDispatch = () => useDispatch();
export const useAppSelector = useSelector;

export const useOnboardingId = () =>
  useAppSelector((state) => state.onboardingId);
export const useToken = () => useAppSelector((state) => state.token);
export const useError = () => useAppSelector((state) => state.error);
export const useBackUrl = () => useAppSelector((state) => state.backUrl);
export const useIsOnboardedWithEmail = () =>
  useAppSelector((state) => state.isOnboardedWithEmail);
export const useEmail = () => useAppSelector((state) => state.email);
export const useFirstName = () => useAppSelector((state) => state.firstName);
export const useLastName = () => useAppSelector((state) => state.lastName);
export const useDateOfBirth = () =>
  useAppSelector((state) => state.dateOfBirth);
export const useCurrentStep = () =>
  useAppSelector((state) => state.currentStep);
export const useCompany = () => useAppSelector((state) => state.company);
export const useIsVOPChecked = () =>
  useAppSelector((state) => state.isVOPChecked);
export const useSignatureLink = () =>
  useAppSelector((state) => state.signatureLink);

export const useApi = () => {
  const dispatch = useDispatch();
  const onboardingId = useOnboardingId();
  const token = useToken();

  const executeRequest = async (apiCall) => {
    try {
      const response = await apiCall();
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const patchOnboarding = async (props) => {
    await executeRequest(() =>
      apiClient.patch(`/onboardings/${onboardingId}`, props, {
        headers: { token },
      }),
    );
    dispatch(updateStore(props));
  };

  const sendOtp = () =>
    executeRequest(() =>
      apiClient.post(
        `/onboardings/${onboardingId}/otp/send`,
        {},
        { headers: { token } },
      ),
    );

  const confirmOtp = (code) =>
    executeRequest(() =>
      apiClient.post(`/onboardings/${onboardingId}/otp/confirm`, code, {
        headers: { token },
      }),
    );

  const searchCompany = (body) =>
    executeRequest(() =>
      apiClient.post(`/Onboardings/${onboardingId}/company/search`, body, {
        headers: { token },
      }),
    );

  const getWorkflow = () => {
    const timestamp = Date.now();
    return executeRequest(() =>
      apiClient.get(
        `/Onboardings/${onboardingId}/workflows?timestamp=${timestamp}`,
        { headers: { token } },
      ),
    );
  };

  const idCheckStart = () =>
    executeRequest(() =>
      apiClient.post(
        `verification/idchecks/onboarding/${onboardingId}/start`,
        {},
        {
          headers: { token },
        },
      ),
    );

  const idCheckCallback = () =>
    executeRequest(() =>
      apiClient.get(`verification/idchecks/onboarding/callback`, {
        headers: { token },
      }),
    );

  const openbankingStart = () =>
    executeRequest(() =>
      apiClient.post(
        `verification/openbanking/onboarding/${onboardingId}/start`,
        {},
        {
          headers: { token },
        },
      ),
    );

  const openbankingCallback = () =>
    executeRequest(() =>
      apiClient.get(`verification/openbanking/onboarding/callback`, {
        headers: { token },
      }),
    );

  const checkVop = (ibanHashed) =>
    executeRequest(() =>
      apiClient.post(
        `verification/sepaMandate/onboarding/${onboardingId}/vop`,
        { iban: ibanHashed },
        {
          headers: { token },
        },
      ),
    );

  const createSepaMandate = () =>
    executeRequest(() =>
      apiClient.post(
        `verification/sepaMandate/onboarding/${onboardingId}`,
        {},
        {
          headers: { token },
        },
      ),
    );

  const getSepaMandate = () =>
    executeRequest(() =>
      apiClient.get(`verification/sepaMandate/onboarding/${onboardingId}`, {
        headers: { token },
      }),
    );

  const inviteCollaborators = (invitees) =>
    executeRequest(() =>
      apiClient.post(
        `/onboardings/${onboardingId}/collaborators/invite`,
        invitees,
        {
          headers: { token },
        },
      ),
    );

  return {
    patchOnboarding,
    sendOtp,
    confirmOtp,
    searchCompany,
    getWorkflow,
    idCheckStart,
    idCheckCallback,
    openbankingStart,
    openbankingCallback,
    checkVop,
    createSepaMandate,
    getSepaMandate,
    inviteCollaborators,
  };
};
